import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';

const NotificationCountsContext = createContext({
  unreadMessages: 0,
  unreadNotifications: 0,
  markProfileNotificationsRead: () => {},
});

export function useNotificationCounts() {
  return useContext(NotificationCountsContext);
}

export function NotificationCountsProvider({ session, children }) {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const fetchUnreadMessages = useCallback(async () => {
    if (!session) return;
    const { count } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('read', false)
      .neq('sender_id', session.user.id);
    setUnreadMessages(count || 0);
  }, [session]);

  const fetchUnreadNotifications = useCallback(async () => {
    if (!session) return;
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', session.user.id)
      .eq('read', false);
    setUnreadNotifications(count || 0);
  }, [session]);

  const markProfileNotificationsRead = useCallback(async () => {
    if (!session) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', session.user.id).eq('read', false);
    setUnreadNotifications(0);
  }, [session]);

  useEffect(() => {
    if (!session) {
      setUnreadMessages(0);
      setUnreadNotifications(0);
      return;
    }
    fetchUnreadMessages();
    fetchUnreadNotifications();

    const channel = supabase
      .channel('notification-counts')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, fetchUnreadMessages)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, fetchUnreadMessages)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, fetchUnreadNotifications)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [session, fetchUnreadMessages, fetchUnreadNotifications]);

  return (
    <NotificationCountsContext.Provider value={{ unreadMessages, unreadNotifications, markProfileNotificationsRead }}>
      {children}
    </NotificationCountsContext.Provider>
  );
}